import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import ThemeContext from "../context/ThemeContext";
import DocumentContext from "../context/DocumentContext";
import AuthContext from "../context/AuthContext";
import PageContext from "../context/PageContext";
import LanguageContext from "../context/LanguageContext";
import { getLocalizedTitle, getLocalizedDescription, getLocalizedAuthor, getTextDirection } from "../utils/languageUtils";
import Pagination from './Pagination';

export default function DocumentViewer() {
    const { documentId } = useParams();
    const { theme } = useContext(ThemeContext);
    const { language } = useContext(LanguageContext);
    const { getDocumentPage, generatePdfForDocument, getPdfUrl } = useContext(DocumentContext);
    const { user } = useContext(AuthContext);
    const { addNewPage, deletePage } = useContext(PageContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    const [document, setDocument] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageData, setPageData] = useState(null);
    const [pageInput, setPageInput] = useState('');
    
    // Full screen mode state
    const [isFullScreen, setIsFullScreen] = useState(false);
    
    // Dialog state
    const [showAddPageDialog, setShowAddPageDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [message, setMessage] = useState({ text: '', type: 'success' });
    const [formData, setFormData] = useState({
        page_number: '',
        english_text: '',
        persian_text: '',
        image: null
    });
    
    // Fetch document page data from backend
    useEffect(() => {
        const fetchDocumentPage = async () => {
            if (!documentId) return;
            
            try {
                setLoading(true);
                setError(null);
                
                // Set document info from location state if available
                if (location.state?.document) {
                    setDocument({
                        id: location.state.document.id,
                        title: location.state.document.name,
                        persian_title: location.state.document.persian_name,
                        author: location.state.document.author,
                        persian_author: location.state.document.persian_author,
                        year: location.state.document.year,
                        description: location.state.document.description,
                        persian_description: location.state.document.persian_description,
                        image_url: location.state.document.image_url,
                        archive_item_id: location.state.document.archive_item_id
                    });
                } else {
                    // Fallback to placeholder values if no document info is passed
                    setDocument({
                        id: documentId,
                        title: "Document",
                        persian_title: "سند",
                        author: "Author",
                        persian_author: "نویسنده",
                        year: "Year",
                        description: "Description",
                        persian_description: "توضیحات",
                        image_url: "/images/placeholder.jpg"
                    });
                }
                
                const data = await getDocumentPage(documentId, currentPage);
                
                // Set page data
                setPageData(data);
                setTotalPages(data.last_page);
                
            } catch (err) {
                // Handle 404 error specifically for no pages
                if (err.message && err.message.includes('404')) {
                    setError("There are no pages in this document.");
                } else {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };
        
        fetchDocumentPage();
    }, [documentId, currentPage, getDocumentPage, location.state]);
    
    // Handle page navigation
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };
    
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle direct page navigation
    const handleGoToPage = (e) => {
        e.preventDefault();
        const pageNum = parseInt(pageInput, 10);
        
        // Validate input
        if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
            // Show error message
            setMessage({ 
                text: `Please enter a valid page number between 1 and ${totalPages}.`, 
                type: 'error' 
            });
            setShowMessage(true);
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
            setPageInput('');
            return;
        }
        
        // Navigate to the page
        setCurrentPage(pageNum);
        setPageInput('');
    };

    // Toggle full screen mode
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'f' || e.key === 'F') {
                toggleFullScreen();
            } else if (e.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [isFullScreen]);

    // Handle admin button clicks
    const handleAddSinglePage = () => {
        console.log(`Add single page clicked for document ID: ${documentId}`);
        setShowAddPageDialog(true);
    };

    const handleGeneratePdf = async () => {
        console.log(`Generate PDF clicked for document ID: ${documentId}`);
        
        try {
            const result = await generatePdfForDocument(documentId);
            
            // Only show success message if server responds with 201
            if (result.status === 201) {
                setMessage({ 
                    text: 'PDF generation completed successfully! Your document PDF is ready.', 
                    type: 'success' 
                });
                setShowMessage(true);
                
                // Hide message after 3 seconds
                setTimeout(() => {
                    setShowMessage(false);
                }, 3000);
            }
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            
            // Show error message
            setMessage({ 
                text: error.message || 'Error generating PDF. Please try again.', 
                type: 'error' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
        }
    };

    const handleDownloadPdf = async () => {
        console.log(`Download PDF clicked for document ID: ${documentId}`);
        
        try {
            // Get the PDF URL from the server
            const pdfUrl = await getPdfUrl(documentId);
            
            // Create a temporary anchor element to trigger the download
            const link = window.document.createElement('a');
            link.href = pdfUrl;
            link.download = `document_${documentId}.pdf`;
            link.target = '_blank';
            
            // Append to body, click, and remove
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            
            // Show success message
            setMessage({ 
                text: 'PDF download started successfully!', 
                type: 'success' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
            
        } catch (error) {
            console.error('Error downloading PDF:', error);
            
            // Show error message
            setMessage({ 
                text: error.message || 'Error downloading PDF. Please try again.', 
                type: 'error' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle file input change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({
            ...prev,
            image: file
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Form submitted with data:');
        console.log('Document ID:', documentId);
        console.log('Page Number:', formData.page_number);
        console.log('English Text:', formData.english_text);
        console.log('Persian Text:', formData.persian_text);
        console.log('Image File:', formData.image);
        console.log('Image Name:', formData.image ? formData.image.name : 'No image selected');
        
        // Create a mock form element to work with the existing addNewPage function
        const mockForm = {
            elements: {
                imageUpload: { files: [formData.image] },
                englishText: { value: formData.english_text },
                persianText: { value: formData.persian_text },
                pageNumber: { value: formData.page_number },
                documentId: { value: documentId }
            },
            reset: () => {
                setFormData({
                    page_number: '',
                    english_text: '',
                    persian_text: '',
                    image: null
                });
            },
            target: e.target
        };
        
        // Add the parent element class to indicate this is a new page (not edit)
        mockForm.parentElement = { classList: { contains: () => false } };
        
        try {
            const result = await addNewPage({ preventDefault: () => {}, target: mockForm });
            
            if (result.success) {
                // Show success message
                setMessage({ 
                    text: result.message, 
                    type: 'success' 
                });
                setShowMessage(true);
                
                // Hide message after 3 seconds
                setTimeout(() => {
                    setShowMessage(false);
                }, 3000);
                
                // Reset form and close dialog
                setFormData({
                    page_number: '',
                    english_text: '',
                    persian_text: '',
                    image: null
                });
                setShowAddPageDialog(false);
                
                // Refresh the current page data to show the new page
                if (documentId) {
                    const data = await getDocumentPage(documentId, currentPage);
                    setPageData(data);
                    setTotalPages(data.last_page);
                }
            } else {
                // Show error message
                setMessage({ 
                    text: result.message, 
                    type: 'error' 
                });
                setShowMessage(true);
                
                // Hide message after 3 seconds
                setTimeout(() => {
                    setShowMessage(false);
                }, 3000);
            }
            
        } catch (error) {
            console.error('Error adding page:', error);
            
            // Show error message
            setMessage({ 
                text: 'Error adding page. Please try again.', 
                type: 'error' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
        }
    };

    // Handle dialog close
    const handleCloseDialog = () => {
        setFormData({
            page_number: '',
            english_text: '',
            persian_text: '',
            image: null
        });
        setShowAddPageDialog(false);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        const pageInfo = {
            documentId: parseInt(documentId),
            currentPage: pageData.current_page
        };
        
        try {
            await deletePage(pageInfo);
            setShowDeleteDialog(false);
            
            // Show success message
            setMessage({ 
                text: `Page ${pageData.current_page} has been successfully deleted.`, 
                type: 'success' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
            
            // Handle navigation after deletion
            if (documentId) {
                try {
                    // Try to load the current page first (in case it's still valid)
                    const data = await getDocumentPage(documentId, currentPage);
                    setPageData(data);
                    setTotalPages(data.last_page);
                } catch (error) {
                    // If current page doesn't exist, try the previous page
                    if (currentPage > 1) {
                        try {
                            const data = await getDocumentPage(documentId, currentPage - 1);
                            setPageData(data);
                            setTotalPages(data.last_page);
                            setCurrentPage(currentPage - 1);
                        } catch (prevError) {
                            // If no pages exist, show error message
                            setError("There are no pages in this document.");
                        }
                    } else {
                        // If it was the first page and it's gone, show error
                        setError("There are no pages in this document.");
                    }
                }
            }
        } catch (error) {
            console.error('Error deleting page:', error);
            setShowDeleteDialog(false);
            
            // Show error message
            setMessage({ 
                text: 'Error deleting page. Please try again.', 
                type: 'error' 
            });
            setShowMessage(true);
            
            // Hide message after 3 seconds
            setTimeout(() => {
                setShowMessage(false);
            }, 3000);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteDialog(false);
    };
    
    return (
        <div className={`document-viewer ${isFullScreen ? 'document-viewer--fullscreen' : ''}`}>
            {/* Header - hidden in full screen mode */}
            {!isFullScreen && (
                <div className="document-viewer__header">
                    <button 
                        onClick={() => {
                            if (document?.archive_item_id) {
                                navigate(`/archive/${document.archive_item_id}`);
                            } else {
                                navigate(-1); // Fallback to browser history
                            }
                        }} 
                        className="back-button"
                    >
                        &larr; Back to Documents 
                    </button>
                    <div className="document-viewer__header-content">
                        <div className="document-viewer__thumbnail">
                            <img 
                                src={document?.image_url || "/images/placeholder.jpg"} 
                                alt={`Thumbnail of ${getLocalizedTitle(document?.title, document?.persian_title, language) || "Document"}`}
                                className="document-viewer__thumbnail-image"
                                onError={(e) => {
                                    e.target.src = "/images/placeholder.jpg";
                                }}
                            />
                        </div>
                        <div className="document-viewer__info">
                            <h1 
                                className="document-viewer__title"
                                dir={getTextDirection(language)}
                            >
                                {getLocalizedTitle(document?.title, document?.persian_title, language) || "Document"}
                            </h1>
                            <div className="document-viewer__meta">
                                <p>By {getLocalizedAuthor(document?.author, document?.persian_author, language) || "Author"}, {document?.year || "Year"}</p>
                            </div>
                            <p 
                                className="document-viewer__description"
                                dir={getTextDirection(language)}
                            >
                                {getLocalizedDescription(document?.description, document?.persian_description, language) || "Description"}
                            </p>
                        </div>
                    </div>
                    
                    {/* Download PDF button - visible to all users */}
                    <div className="document-viewer__download-section">
                        <button 
                            onClick={handleDownloadPdf}
                            className="document-viewer__admin-button"
                        >
                            Download PDF
                        </button>
                    </div>
                    
                    {/* Admin buttons for logged-in users */}
                    {user && (
                        <div className="document-viewer__admin-buttons">
                            <button 
                                onClick={handleAddSinglePage}
                                className="document-viewer__admin-button"
                            >
                                Add Single Page
                            </button>
                            <button 
                                onClick={handleGeneratePdf}
                                className="document-viewer__admin-button"
                            >
                                Generate PDF
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            {/* Message Display */}
            {showMessage && (
                <div className={`document-viewer__message document-viewer__message--${message.type}`}>
                    <span className="document-viewer__message-text">{message.text}</span>
                    <button 
                        className="document-viewer__message-close"
                        onClick={() => setShowMessage(false)}
                    >
                        ×
                    </button>
                </div>
            )}
            
            {/* Loading state */}
            {loading && (
                <div className="document-viewer__loading">
                    <p>Loading document...</p>
                </div>
            )}
            
            {/* Error state */}
            {error && (
                <div className="document-viewer__error">
                    <p>{error}</p>
                    <Link to="/archive" className="document-viewer__back-button">
                        Return to Archive
                    </Link>
                </div>
            )}
            
            {/* Document not found state */}
            {!loading && !error && (!document || !pageData) && (
                <div className="document-viewer__error">
                    <h2>Document Not Found</h2>
                    <p>The requested document could not be found.</p>
                    <Link to="/archive" className="document-viewer__back-button">
                        Return to Archive
                    </Link>
                </div>
            )}
            
            {/* Document content - only show when data is available */}
            {!loading && !error && document && pageData && (
                <>
                    <div className="document-viewer__navigation">
                        <div className="document-viewer__page-info">
                            Page {pageData.current_page} of {pageData.last_page}
                        </div>
                        <div className="document-viewer__controls">
                            <Pagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                            <form onSubmit={handleGoToPage} className="document-viewer__goto-form">
                                <label htmlFor="goto-page" className="document-viewer__goto-label">
                                    Go to page:
                                </label>
                                <input
                                    type="number"
                                    id="goto-page"
                                    min="1"
                                    max={totalPages}
                                    value={pageInput}
                                    onChange={(e) => setPageInput(e.target.value)}
                                    placeholder="Page #"
                                    className="document-viewer__goto-input"
                                />
                                <button 
                                    type="submit"
                                    className="document-viewer__goto-button"
                                >
                                    Go
                                </button>
                            </form>
                            <button 
                                onClick={toggleFullScreen}
                                className="document-viewer__fullscreen-button"
                                title={isFullScreen ? "Exit Full Screen (F or Esc)" : "Enter Full Screen (F)"}
                            >
                                {isFullScreen ? "⤓ Exit Full Screen" : "⤢ Full Screen"}
                            </button>
                        </div>
                    </div>
                    
                    <div className="document-viewer__content">
                        <div className="document-viewer__image-container">
                            <img 
                                src={pageData.image_url} 
                                alt={`Page ${pageData.current_page} of ${getLocalizedTitle(document?.title, document?.persian_title, language)}`}
                                className="document-viewer__image"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            {user && !isFullScreen && (
                                <button 
                                    onClick={() => {
                                        console.log(`Delete page clicked for document ID: ${documentId}, page number: ${pageData.current_page}`);
                                        setShowDeleteDialog(true);
                                    }}
                                    className="document-viewer__delete-button"
                                >
                                    Delete Page
                                </button>
                            )}
                        </div>
                        
                        {/* Transcriptions - hidden in full screen mode */}
                        {!isFullScreen && (
                            <div className="document-viewer__transcriptions">
                                {pageData.english_text && pageData.english_text.trim() !== '' && (
                                    <div className="document-viewer__transcription document-viewer__transcription--english">
                                        <h3 className="document-viewer__transcription-title">English Translation</h3>
                                        <div className="document-viewer__transcription-content">
                                            {pageData.english_text}
                                        </div>
                                    </div>
                                )}
                                
                                {pageData.persian_text && pageData.persian_text.trim() !== '' && (
                                    <div className="document-viewer__transcription document-viewer__transcription--persian">
                                        <h3 className="document-viewer__transcription-title" dir="rtl">متن فارسی</h3>
                                        <div className="document-viewer__transcription-content" dir="rtl">
                                            {pageData.persian_text}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Add Single Page Dialog */}
            {showAddPageDialog && (
                <>
                    <div className="dialog-overlay" onClick={handleCloseDialog} />
                    <div className="dialog">
                        <h2 className="dialog__title">Add Single Page</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="page_number">Page Number:</label>
                                <input
                                    type="text"
                                    id="page_number"
                                    name="page_number"
                                    value={formData.page_number}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter page number..."
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="english_text">English Text:</label>
                                <textarea
                                    id="english_text"
                                    name="english_text"
                                    value={formData.english_text}
                                    onChange={handleInputChange}
                                    required
                                    rows="4"
                                    placeholder="Enter English text..."
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="persian_text">Persian Text:</label>
                                <textarea
                                    id="persian_text"
                                    name="persian_text"
                                    value={formData.persian_text}
                                    onChange={handleInputChange}
                                    required
                                    rows="4"
                                    placeholder="متن فارسی را وارد کنید..."
                                    dir="rtl"
                                    lang="fa"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="image">Image:</label>
                                <input
                                    type="file"
                                    id="image"
                                    name="image"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    required
                                />
                            </div>
                            
                            <div className="dialog__buttons">
                                <button 
                                    type="button" 
                                    className="dialog__button dialog__button--cancel"
                                    onClick={handleCloseDialog}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="dialog__button dialog__button--submit"
                                >
                                    Add Page
                                </button>
                            </div>
                        </form>
                    </div>
                </>
            )}

            {/* Delete Page Confirmation Dialog */}
            {showDeleteDialog && (
                <>
                    <div className="dialog-overlay" onClick={handleDeleteCancel} />
                    <div className="dialog">
                        <h2 className="dialog__title">Delete Page</h2>
                        <div className="dialog__content">
                            <p>Are you sure you want to delete page {pageData?.current_page}?</p>
                            <p>This action cannot be undone.</p>
                        </div>
                        <div className="dialog__buttons">
                            <button 
                                type="button" 
                                className="dialog__button dialog__button--cancel"
                                onClick={handleDeleteCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="dialog__button dialog__button--delete"
                                onClick={handleDeleteConfirm}
                            >
                                Delete Page
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
} 